import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { OrganicFertilizerType } from '~/api/services/climaCheck'
import { FormFields } from '~/components/FormFields'
import { OptionContent } from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ForageProdStep/style'
import { Popup, Table } from 'semantic-ui-react'
import { formatNumber } from '~/utils/helpers'
import CustomIcon from '~/components/CustomIcon'
import InfoPopup from '~/components/InfoPopup'
import EditFertilizers from './EditFertilizers'
import { StepId } from '../../../constants'
import Total from './Total'
import { CO2OptionsType } from '~/api/services/co2'

export const fertilizerDataFields = [
  {
    name: 'N',
    key: 'n',
    unit: 'kg/t bzw. kg/m³'
  },
  {
    name: '<span>P<sub>2</sub>O<sub>5</sub><span>',
    key: 'p2o5',
    unit: 'kg/t bzw. kg/m³'
  },
  {
    name: '<span>K<sub>2</sub>O</span>',
    key: 'k2o',
    unit: 'kg/t bzw. kg/m³'
  }
]

interface Props {
  organicKey: 'grassSilage.organicFertilizers' | 'maizeSilage.organicFertilizers'
  options: CO2OptionsType
}

const OrganicFertilizers: React.FC<Props> = ({ organicKey, options }) => {
  const { t } = useTranslation()
  const { organicFertilizers = [], silageTypes = [] } = options

  const applicationTechniques = silageTypes.find(silageType => silageType.slug === StepId.GRASS_SILAGE)?.organicFertilizerApplicationTechniques || []

  const { control, getValues, clearErrors, setValue } = useFormContext()

  const { fields, replace, update } = useFieldArray({
    control,
    name: organicKey,
    keyName: 'key'
  })

  const [fertilizerData, setFertilizerData] = useState<OrganicFertilizerType>(null)

  const tableColumnHeaders = [
    {
      name: 'Düngeart',
      key: 'label',
      type: 'fertilizer-fields'
    },
    {
      name: 'Ausbringmenge',
      key: 'value',
      type: 'input'
    },
    {
      name: 'Ausbringtechnik',
      key: 'co2OrganicFertilizerApplicationTechniqueId',
      type: 'select',
      optionsKey: 'organicFertilizerApplicationTechniques'
    }
  ];

  function renderInputCellValue(name, unit: string) {
    const unitTransformed = `${unit?.toString()?.startsWith('m3') ? 'm³' : unit}/ha`
    return <FormFields.Input
      placeholder={`in ${unitTransformed}`}
      name={name}
      id={name}
      onChange={(event) => {
        setValue(name, event?.target?.value?.replace(unitTransformed, ''))
        clearErrors(name)
      }}
      rules={{
        required: true
      }}
      control={control}
      suffix={` ${unitTransformed}`}
      typeInput={'controled'}
      withoutAsterisk
      noErrorMessage
      allowDecimals
      decimalScale={2}
    />
  }

  function renderSelect(name) {
    return <FormFields.Select
      name={name}
      style={{ width: 380 }}
      placeholder={'Art der Ausbringungstechhnik'}
      control={control}
      onChange={(e, data) => {
        setValue(name, data.value)
        clearErrors(name)
      }}
      rules={{
        required: true
      }}
      className={'menu-contain'}
      upward
      scrolling
      withoutAsterisk
      noErrorMessage
      options={applicationTechniques.map(orgFertAppTechnique => ({ value: orgFertAppTechnique.id, text: orgFertAppTechnique.label }))}
    />
  }

  function renderFertilizerFieldCell(organicFertilizer) {
    return <>
      <div>{organicFertilizer.label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <small style={{ fontSize: '85%' }}>
          <>{`N: ${organicFertilizer.n} kg/t,  `}</>
          <>P<sub>2</sub>O<sub>5</sub>{`: ${organicFertilizer.p2o5} kg/t, `}</>
          <>K<sub>2</sub>O{`: ${organicFertilizer.k2o} kg/t`}</>
        </small>
        <div style={{ display: 'flex', gap: 5, marginLeft: 7 }}>
          <InfoPopup
            position={'top center'}
            styleIcon={{ fontSize: '1.2em' }}
            content={<small style={{ fontSize: '85%' }}>
              {fertilizerDataFields.map(fertilizeField => {
                return <div
                  key={fertilizeField.key}
                  style={{ wordBreak: 'keep-all' }}
                  dangerouslySetInnerHTML={{
                    __html: `${fertilizeField.name}: 
                       ${formatNumber(organicFertilizer[fertilizeField.key]?.replace(',', '.'), 2)} 
                       kg/${organicFertilizer.unit} <span style="font-style: italic; font-size: 12px !important;">(Nutzbar: ${organicFertilizer[`${fertilizeField.key}Usable`]}%)</span>`
                  }}
                />
              })}
            </small>}
          />
          <Popup
            content={<div>
              <div>{t('Change')}</div>
            </div>}
            trigger={<CustomIcon
              name={'pencil'}
              onClick={() => setFertilizerData(organicFertilizer)}
              style={{ fontSize: '1.2em' }}
            />}
          />
        </div>
      </div>
    </>
  }

  function getTotal(organicFertilizer) {
    const value = parseFloat(organicFertilizer?.value?.replace(',', '.')) || 0
    const nitroValue = parseFloat(organicFertilizer?.n?.replace(',', '.')) || 0
    return `${formatNumber(value * nitroValue)} kg N/m3`
  }

  function renderCell(organicFertilizer, name, column) {
    switch (column.type) {
      case 'input':
        return renderInputCellValue(name, organicFertilizer?.unit)
      case 'select':
        return renderSelect(name)
      case 'fertilizer-fields':
        return renderFertilizerFieldCell(organicFertilizer)
      case 'total':
        return getTotal(organicFertilizer)
      default:
        return organicFertilizer[column.key]
    }
  }

  function renderTableBody() {
    return fields.map((organicFertilizer, indexFertilizer) => {
      return <Table.Row key={organicFertilizer.key}>
        {tableColumnHeaders.map(column => {
          const name = `${organicKey}[${indexFertilizer}].${column.key}`
          return <Table.Cell key={`${organicFertilizer.key}-${column.key}`}>
            {renderCell(organicFertilizer, name, column)}
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderTable() {
    return <Table columns={3}>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(header => {
            return <Table.HeaderCell key={header.key}>
              {header.name}
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  function renderEditFertilizerData() {
    return <EditFertilizers
      fertilizerData={fertilizerData}
      handleClose={() => setFertilizerData(null)}
      handleSubmit={(fertilizerData, updateData) => {
        const index = fields.findIndex(fertilizer => fertilizer['id'] === fertilizerData.id)

        if (index >= 0) {
          update(index, {
            ...fertilizerData,
            ...updateData
          })
          setFertilizerData(null)
        }
      }}
    />
  }

  return <>
    {renderEditFertilizerData()}
    <OptionContent>
      <FormFields.Select
        label={'Welchen Wirtschaftsdünger haben Sie aufgebracht?'}
        control={control}
        id={organicKey}
        placeholder='Art des Wirtschaftsdünger'
        name=''
        value={fields.map(fertilizer => fertilizer['id'])}
        options={organicFertilizers.map(type => ({ value: type.id, text: type.label }))}
        onChange={(_, { value }: { value: number[] }) => {
          const organicFertilizersValue = value.map(id => organicFertilizers.find(fertilizer => fertilizer.id === id))
          const fertilizers = getValues(organicKey)
          const includedFertilizerValues = value.map(id => fertilizers.find(field => field['id'] === id) || organicFertilizersValue.find(n => n.id === id))
          if (includedFertilizerValues.length > 0) {
            const targetElement = document.getElementById(organicKey)
            targetElement?.classList?.remove('error');
          }
          replace(includedFertilizerValues)
        }}
        multiple
        search
        noResultsMessage={t('No results found....')}
        noErrorMessage
        withoutAsterisk
      />
      {fields.length > 0 && renderTable()}
      {fields.length > 0 && <div style={{ display: 'flex', alignItems: 'flex-start', gap: 30, flexWrap: 'wrap' }}>
        <Total organicKey={organicKey} />
        <Total withPercentage organicKey={organicKey} />
      </div>}
    </OptionContent>
  </>
}
export default OrganicFertilizers